/**
 * Generates mock data for portfolio components.
 */
import {
  PortfolioSummaryData,
  StrategyPerformanceData,
  RiskMetric,
  TradeData,
  MonthlyPerformanceData,
  AIInsightData,
  EquityDataPoint,
  AllocationDataPoint,
} from './types';
import { format, subDays, subMonths, startOfMonth } from 'date-fns';

const SYMBOLS = ['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'META', 'TSLA', 'NVDA', 'JPM', 'V', 'JNJ'];
const STRATEGY_NAMES = [
  'Momentum Alpha', 'Volatility Edge', 'Dividend Growth', 'Tech Titans', 'Global Macro',
  'Value Discovery', 'Arbitrage Finder', 'Income Generator', 'Emerging Markets', 'Sector Rotation'
];

// Export this to make it accessible in other files
export const ALLOCATION_TYPES = [
  { name: 'US Equities', color: '#3b82f6' },   // blue-500
  { name: 'Intl Equities', color: '#10b981' }, // green-500
  { name: 'Bonds', color: '#f59e0b' },         // amber-500
  { name: 'Commodities', color: '#ef4444' },   // red-500
  { name: 'Real Estate', color: '#a855f7' },  // purple-500
  { name: 'Cash', color: '#6b7280' },          // gray-500
];

const RISK_NAMES = ['Max Drawdown', 'Volatility (Ann.)', 'Sharpe Ratio', 'Exposure', 'VaR (95%, 1d)'];
const AI_CATEGORIES: AIInsightData['category'][] = ['Market', 'Risk', 'Opportunity', 'Portfolio'];
const AI_SEVERITIES: AIInsightData['severity'][] = ['info', 'warning', 'critical'];

// Helper to generate random number in range
const random = (min: number, max: number): number => Math.random() * (max - min) + min;

// Helper to format date as YYYY-MM-DD
const formatDate = (date: Date): string => format(date, 'yyyy-MM-dd');

// Generate Equity Curve Data
export const generateEquityCurve = (days: number = 90, startValue: number = 1000000): EquityDataPoint[] => {
  const data: EquityDataPoint[] = [];
  let currentValue = startValue;
  const today = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const date = subDays(today, i);
    const changePercent = (random(-1.5, 1.8)) / 100; // Daily change between -1.5% and +1.8%
    currentValue *= (1 + changePercent);
    data.push({ date: formatDate(date), value: parseFloat(currentValue.toFixed(2)) });
  }
  return data;
};

// Generate Allocation Data
export const generateAllocation = (totalValue: number): AllocationDataPoint[] => {
  const allocation: AllocationDataPoint[] = [];
  let remainingPercent = 100;
  // Use a fixed set of types for consistency in the pie chart
  const typesToUse = ALLOCATION_TYPES.slice(0, Math.floor(random(4, ALLOCATION_TYPES.length + 1))); // Use 4 to 6 types

  for (let i = 0; i < typesToUse.length - 1; i++) {
      const percent = random(5, Math.min(35, remainingPercent - (typesToUse.length - 1 - i) * 5)); // Ensure remaining have at least 5%
      remainingPercent -= percent;
      allocation.push({
          name: typesToUse[i].name,
          value: parseFloat(percent.toFixed(1)),
          color: typesToUse[i].color,
      });
  }
  // Assign remaining to the last type
  allocation.push({
      name: typesToUse[typesToUse.length - 1].name,
      value: parseFloat(remainingPercent.toFixed(1)),
      color: typesToUse[typesToUse.length - 1].color,
  });

  // Ensure percentages sum roughly to 100 (adjust last element if needed)
  const totalPercent = allocation.reduce((sum, item) => sum + item.value, 0);
  if (totalPercent !== 100 && allocation.length > 0) {
      const diff = 100 - totalPercent;
      allocation[allocation.length - 1].value = parseFloat((allocation[allocation.length - 1].value + diff).toFixed(1));
      // Ensure no negative percentages after adjustment
      if(allocation[allocation.length - 1].value < 0) {
         // Basic redistribution if adjustment made it negative (rare case)
         let deficit = Math.abs(allocation[allocation.length - 1].value);
         allocation[allocation.length - 1].value = 0;
         for(let i = 0; i < allocation.length -1 && deficit > 0; i++) {
            let take = Math.min(allocation[i].value, deficit);
            allocation[i].value = parseFloat((allocation[i].value - take).toFixed(1));
            deficit -= take;
         }
      }
  }


  return allocation;
};


// Generate Portfolio Summary
export const generatePortfolioSummary = (): PortfolioSummaryData => {
  const equityCurve = generateEquityCurve();
  const totalValue = equityCurve[equityCurve.length - 1].value;
  const prevDayValue = equityCurve[equityCurve.length - 2]?.value || totalValue;
  const todayChangeValue = totalValue - prevDayValue;
  const todayChangePercent = prevDayValue === 0 ? 0 : (todayChangeValue / prevDayValue) * 100;

  return {
    totalValue,
    todayChangeValue,
    todayChangePercent,
    monthlyChangePercent: random(-5, 8),
    ytdChangePercent: random(5, 25),
    equityCurve,
    allocation: generateAllocation(totalValue),
  };
};

// Fix generateTopStrategies to include source property
export const generateTopStrategies = (count: number): StrategyPerformanceData[] => {
  const strategies: StrategyPerformanceData[] = [];
  const usedNames = new Set<string>();

  while (strategies.length < count && usedNames.size < STRATEGY_NAMES.length) {
    const nameIndex = Math.floor(random(0, STRATEGY_NAMES.length));
    const name = STRATEGY_NAMES[nameIndex];
    if (!usedNames.has(name)) {
      usedNames.add(name);
      strategies.push({
        id: `strat_${random(1000, 9999)}`,
        name: name,
        source: 'internal', // Add source property
        ytdReturn: random(-10, 35),
        status: Math.random() > 0.8 ? 'inactive' : 'active',
        riskLevel: ['low', 'medium', 'high'][Math.floor(random(0, 3))] as 'low' | 'medium' | 'high',
      });
    }
  }
  // Sort by YTD return descending
  return strategies.sort((a, b) => b.ytdReturn - a.ytdReturn);
};

// Generate Risk Metrics
export const generateRiskMetrics = (): RiskMetric[] => {
  const metrics: RiskMetric[] = [];
  const drawdown = random(5, 25); // Max Drawdown %
  const volatility = random(8, 30); // Volatility %
  const sharpe = random(0.5, 2.5); // Sharpe Ratio
  const exposure = random(40, 110); // Exposure %
  const varValue = random(0.5, 3.0); // VaR %

  const getLevel = (value: number, thresholds: [number, number]): 'low' | 'medium' | 'high' => {
      if (value <= thresholds[0]) return 'low';
      if (value <= thresholds[1]) return 'medium';
      return 'high';
  };

  metrics.push({
    id: 'drawdown', name: 'Max Drawdown', value: `-${drawdown.toFixed(1)}%`,
    level: getLevel(drawdown, [10, 20]), // Higher is worse
    description: 'Maximum peak-to-trough decline in portfolio value.',
  });
  metrics.push({
    id: 'volatility', name: 'Volatility (Ann.)', value: `${volatility.toFixed(1)}%`,
    level: getLevel(volatility, [15, 25]), // Higher is riskier
    description: 'Annualized standard deviation of portfolio returns.',
  });

  let sharpeLevel: 'low' | 'medium' | 'high';
  if (sharpe > 1.5) sharpeLevel = 'low'; // High sharpe = low risk
  else if (sharpe > 1.0) sharpeLevel = 'medium';
  else sharpeLevel = 'high'; // Low sharpe = high risk

  metrics.push({
    id: 'sharpe', name: 'Sharpe Ratio', value: sharpe.toFixed(2),
    level: sharpeLevel,
    description: 'Risk-adjusted return (higher is better).',
  });
  metrics.push({
    id: 'exposure', name: 'Net Exposure', value: `${exposure.toFixed(0)}%`,
    level: getLevel(exposure, [70, 100]), // Higher is riskier
    description: 'Total market exposure relative to portfolio value.',
  });
   metrics.push({
    id: 'var', name: 'Value-at-Risk (VaR)', value: `-${varValue.toFixed(1)}%`,
    level: getLevel(varValue, [1.5, 2.5]), // Higher is worse
    description: 'Estimated maximum loss expected in one day with 95% confidence.',
  });

  return metrics;
};

// Generate Recent Trades
export const generateRecentTrades = (count: number): TradeData[] => {
  const trades: TradeData[] = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const symbol = SYMBOLS[Math.floor(random(0, SYMBOLS.length))];
    const action = Math.random() > 0.5 ? 'buy' : 'sell';
    const quantity = Math.floor(random(10, 500));
    const price = random(50, 500);
    const timestamp = subDays(now, Math.floor(random(0, 5))).toISOString();
    const status = Math.random() > 0.9 ? 'failed' : Math.random() > 0.8 ? 'pending' : 'filled';

    trades.push({
      id: `trade_${random(10000, 99999)}`,
      symbol,
      action,
      quantity,
      price: parseFloat(price.toFixed(2)),
      timestamp,
      status: status as 'filled' | 'pending' | 'failed',
    });
  }
  // Sort by timestamp descending
  return trades.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
};

// Generate Monthly Performance Data
export const generateMonthlyPerformance = (): MonthlyPerformanceData => {
    const lastMonth = subMonths(new Date(), 1);
    const monthName = format(lastMonth, 'MMMM yyyy');
    const returnPercent = random(-8, 12);
    const benchmarkReturnPercent = returnPercent + random(-2, 2); // Simulate tracking error
    const contributors = SYMBOLS.sort(() => 0.5 - Math.random());

    return {
        month: monthName,
        returnPercent: parseFloat(returnPercent.toFixed(2)),
        benchmarkReturnPercent: parseFloat(benchmarkReturnPercent.toFixed(2)),
        topContributor: contributors[0],
        bottomContributor: contributors[1],
        keyEvents: [
            `Market reacted to Fed rate decision on ${format(subDays(startOfMonth(lastMonth), -15), 'MMMM do')}.`,
            `Sector rotation observed mid-month towards ${ALLOCATION_TYPES[Math.floor(random(0,3))].name}.`,
            `Earnings surprise for ${contributors[2]} impacted performance.`
        ],
        aiCommentary: `The portfolio ${returnPercent > 0 ? 'outperformed' : 'underperformed'} the benchmark by ${Math.abs(returnPercent - benchmarkReturnPercent).toFixed(1)}%. ${returnPercent > 0 ? `Strong performance in ${contributors[0]} drove returns.` : `Losses in ${contributors[1]} weighed on the portfolio.`} Market volatility was ${random(0,1) > 0.5 ? 'elevated' : 'subdued'} during the period.`
    };
};

// Generate AI Insight
export const generateAIInsight = (): AIInsightData => {
    const category = AI_CATEGORIES[Math.floor(random(0, AI_CATEGORIES.length))];
    const severity = AI_SEVERITIES[Math.floor(random(0, AI_SEVERITIES.length))];
    let text = '';

    switch (category) {
        case 'Market': text = `Increased volatility observed in the ${ALLOCATION_TYPES[Math.floor(random(0,3))].name} sector. Consider adjusting exposure.`; break;
        case 'Risk': text = `Portfolio concentration in ${SYMBOLS[Math.floor(random(0, SYMBOLS.length))]} exceeds the recommended 10% threshold. Review position size.`; break;
        case 'Opportunity': text = `Bullish signal detected for ${SYMBOLS[Math.floor(random(0, SYMBOLS.length))]} based on recent price action and volume surge. Potential entry point identified.`; break;
        case 'Portfolio': text = `Overall portfolio diversification is strong, but correlation between ${SYMBOLS[0]} and ${SYMBOLS[1]} has increased. Monitor closely.`; break;
    }

    if (severity === 'warning') text = `Warning: ${text}`;
    if (severity === 'critical') text = `Critical Alert: ${text}`;

    return {
        id: `insight_${Date.now()}`,
        timestamp: new Date().toISOString(),
        category,
        text,
        severity: severity !== 'info' ? severity : undefined,
        model_used: 'fallback_model' // Indicate this is mock/fallback data
    };
};
